import { LoginFlow, UpdateLoginFlowBody } from "@ory/client";
import { UserAuthCard } from "@ory/elements";
import { useEffect, useState } from "react";
import { Authentication } from "@pages/authentication/Authentication";
import { useNavigate } from "react-router";
import { routes } from "@routes";
import { getConnectedUser } from "@services/user";
import { useSearchParams } from "react-router";
import { Spinner } from "@thetreep/ui";
import { ErrorData, handleError } from "@pages/authentication/error";
import { ory } from "@services/authentication";
import { useUserStore } from "@stores/userStore";

export function Login({ onSwitch }: { onSwitch?: () => void }) {
  const [flow, setFlow] = useState<LoginFlow | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnTo = window.location.origin;
  const { setUser } = useUserStore();
  const errorHandler: (error: ErrorData) => unknown = handleError(
    (flowId: string) => getFlow(flowId),
    setFlow,
    routes.REGISTRATION,
    true,
    navigate,
  );

  const getFlow = async (flowId: string) => {
    try {
      const res = await ory().getLoginFlow({ id: flowId });
      setFlow(res.data);
    } catch (e) {
      errorHandler(e as ErrorData);
    }
  };

  const createFlow = async () => {
    try {
      const loginChallenge = searchParams.get("login_challenge");
      const res = await ory().createBrowserLoginFlow({
        ...(returnTo && { returnTo }),
        ...(loginChallenge && { loginChallenge }),
      });

      if (!onSwitch) {
        setSearchParams({ flow: res.data.id });
      }
      setFlow(res.data);
    } catch (error) {
      errorHandler(error as ErrorData);
    }
  };

  const submitFlow = async (body: UpdateLoginFlowBody) => {
    if (!flow) {
      navigate(routes.REGISTRATION, { replace: true });
      return;
    }

    try {
      await ory().updateLoginFlow({
        flow: flow.id,
        updateLoginFlowBody: body,
      });
      const user = await getConnectedUser();
      if (!user) {
        throw "could not get user";
      }
      setUser(user);
      navigate("/", { replace: true });
    } catch (error) {
      errorHandler(error as ErrorData);
    }
  };

  useEffect(() => {
    (async () => {
      const user = await getConnectedUser();
      if (user) {
        setUser(user);
        navigate("/");
        return;
      }
      const flowId = onSwitch ? null : searchParams.get("flow");
      if (!flow && flowId) {
        getFlow(flowId);
        return;
      }
      createFlow();
    })();
  }, []);

  if (!flow) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <Authentication>
      <UserAuthCard
        flowType="login"
        className="authCard"
        flow={flow}
        additionalProps={{
          forgotPasswordURL: {
            handler: () => {
              const search = new URLSearchParams();
              if (flow.return_to) {
                search.set("return_to", flow.return_to);
              }
              navigate(
                {
                  pathname: routes.RECOVERY,
                  search: search.toString(),
                },
                { replace: true },
              );
            },
          },
          signupURL: {
            handler: () => {
              const search = new URLSearchParams();
              if (flow.return_to) {
                search.set("return_to", flow.return_to);
              }
              if (flow.oauth2_login_challenge) {
                search.set("login_challenge", flow.oauth2_login_challenge);
              }

              if (onSwitch) {
                onSwitch();
              } else {
                navigate(
                  {
                    pathname: routes.REGISTRATION,
                    search: search.toString(),
                  },
                  { replace: true },
                );
              }
            },
          },
        }}
        includeScripts={true}
        onSubmit={({ body }) => submitFlow(body as UpdateLoginFlowBody)}
      />
    </Authentication>
  );
}
